// frontend/src/pages/SupplierPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import { PhotoIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

const BACKEND_URL = api.defaults.baseURL.replace('/api', '');

const SupplierPage = () => {
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentStatus, setCurrentStatus] = useState('Pending');
    const { token } = useParams();

    // Estados para tracking input
    const [trackingInputs, setTrackingInputs] = useState({});
    const [updatingItemId, setUpdatingItemId] = useState(null);

    // Buscar itens
    const fetchSupplierItems = useCallback(async (tokenToUse, statusToFetch) => {
        if (!tokenToUse) {
            setError('Token de acesso ausente.');
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        setError('');

        try {
            const response = await api.get(`/supplier-view/${tokenToUse}?status=${statusToFetch}`);
            setItems(response.data || []);
        } catch (err) {
            console.error("[Supplier Page] Erro ao buscar dados:", err);
            const errorMsg = err.response?.data?.message || 'Falha ao carregar dados. Verifique o link de acesso.';
            setError(errorMsg);
            setItems([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSupplierItems(token, currentStatus);
    }, [token, currentStatus, fetchSupplierItems]);

    // Handler para marcar como Ready
    const handleReadyToShipChange = async (lineItemId, currentValue) => {
        setUpdatingItemId(lineItemId);
        try {
            await api.put(`/supplier-view/${token}/items/${lineItemId}/ready`, {
                isReadyToShip: !currentValue
            });
            // Atualiza o item localmente
            setItems(prevItems =>
                prevItems.map(item =>
                item.lineItemId === lineItemId
                        ? { ...item, isReadyToShip: !currentValue }
                    : item
                )
            );
        } catch (err) {
            console.error('Erro ao atualizar Ready:', err);
            alert('Erro ao atualizar. Tente novamente.');
        } finally {
            setUpdatingItemId(null);
        }
    };

    // Handler para enviar tracking (pode ser para múltiplos itens do mesmo pedido)
    const handleTrackingSubmit = async (orderKey, itemIds) => {
        const trackingNumber = trackingInputs[orderKey]?.trim();
        if (!trackingNumber) {
            alert('Por favor, insira um código de rastreio.');
            return;
        }

        setUpdatingItemId(orderKey);
        try {
            // Atualiza todos os itens do pedido com o mesmo tracking
            await Promise.all(
                itemIds.map(id => 
                    api.put(`/supplier-view/${token}/items/${id}/tracking`, {
                        trackingNumber
                    })
                )
            );
            alert('Código de rastreio enviado com sucesso!');
            // Remove item da lista ou atualiza status
            fetchSupplierItems(token, currentStatus);
            setTrackingInputs(prev => ({ ...prev, [orderKey]: '' }));
        } catch (err) {
            console.error('Erro ao enviar tracking:', err);
            alert(err.response?.data?.message || 'Erro ao enviar código de rastreio.');
        } finally {
            setUpdatingItemId(null);
        }
    };

    // Agrupar itens por pedido (base do orderNumber)
    const groupedOrders = React.useMemo(() => {
        const groups = {};
        items.forEach(item => {
            // Extrai o número base do pedido (ex: "13176-1" -> "13176")
            const baseOrderNumber = item.orderNumber.split('-')[0];
            if (!groups[baseOrderNumber]) {
                groups[baseOrderNumber] = {
                    orderNumber: baseOrderNumber,
                    items: [],
                    customer: item.customer,
                };
            }
            groups[baseOrderNumber].items.push(item);
        });
        return Object.values(groups);
    }, [items]);

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                {/* Título */}
                <h1 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                订单处理 / Order Processing
            </h1>

                {/* Botões de Filtro */}
                <div className="flex justify-center space-x-4 mb-8">
                <button
                        onClick={() => setCurrentStatus('Pending')}
                        className={`px-6 py-3 rounded-lg text-sm font-medium transition-colors ${
                            currentStatus === 'Pending'
                                ? 'bg-blue-600 text-white shadow-lg'
                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                        }`}
                >
                    待处理 / Pending
                </button>
                <button
                        onClick={() => setCurrentStatus('ToBeShipped')}
                        className={`px-6 py-3 rounded-lg text-sm font-medium transition-colors ${
                            currentStatus === 'ToBeShipped'
                                ? 'bg-purple-600 text-white shadow-lg'
                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                        }`}
                >
                    待发货 / To be Shipped
                </button>
                <button
                        onClick={() => setCurrentStatus('Shipped')}
                        className={`px-6 py-3 rounded-lg text-sm font-medium transition-colors ${
                        currentStatus === 'Shipped'
                                ? 'bg-green-600 text-white shadow-lg'
                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                    >
                    已发货 / Shipped
                </button>
            </div>

                {/* Loading */}
                {isLoading && (
                    <div className="text-center py-12">
                        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                        <p className="mt-4 text-gray-600">加载中... / Loading...</p>
                    </div>
                )}

                {/* Erro */}
                {error && !isLoading && (
                    <div className="max-w-2xl mx-auto bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
                        {error}
                    </div>
                )}

                {/* Lista de Pedidos Agrupados */}
            {!isLoading && !error && (
                    <div className="space-y-6">
                        {groupedOrders.length === 0 ? (
                            <p className="text-center text-gray-500 py-12">
                                没有找到订单 / No orders found.
                            </p>
                        ) : (
                            groupedOrders.map((order) => (
                                <div
                                    key={order.orderNumber}
                                    className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
                                >
                                    {/* Cabeçalho do Pedido */}
                                    <div className="bg-gray-100 px-6 py-4 border-b border-gray-200">
                                        <h3 className="text-xl font-bold text-gray-900">
                                            订单 / Order #{order.orderNumber}
                                        </h3>
                                        <p className="text-sm text-gray-600 mt-1">
                                            {order.items.length} {order.items.length === 1 ? 'item' : 'itens'}
                                        </p>
                                    </div>

                                    <div className="p-6">
                                        {/* Lista de Itens do Pedido */}
                                        <div className="space-y-4 mb-6">
                                            {order.items.map((item, index) => (
                                                <div key={item.lineItemId} className="flex items-start space-x-4 pb-4 border-b border-gray-100 last:border-b-0">
                                                    {/* Imagem do Item */}
                                                    <div className="w-20 h-20 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                                                {item.variantImageUrl ? (
                                                            <img
                                                                src={`${BACKEND_URL}${item.variantImageUrl}`}
                                                                alt={item.variantTitle}
                                                                className="w-full h-full object-contain rounded"
                                                            />
                                                        ) : (
                                                            <PhotoIcon className="h-10 w-10 text-gray-400" />
                                                        )}
                                                    </div>
                                                    
                                                    {/* Info do Item */}
                                                    <div className="flex-1">
                                                        <p className="text-sm font-medium text-gray-900">
                                                            Item {index + 1}: {item.variantTitle || 'N/A'}
                                                        </p>
                                                        {item.variantCost && (
                                                            <p className="text-sm text-gray-600 mt-1">
                                                                价格 / Cost: ${item.variantCost}
                                                            </p>
                                                        )}
                                                        
                                                        {/* Checkbox Ready (só na aba Pending) */}
                                                        {currentStatus === 'Pending' && (
                                                            <label className="flex items-center space-x-2 mt-2 cursor-pointer">
                                                            <input
                                                                    type="checkbox"
                                                                    checked={item.isReadyToShip || false}
                                                                    onChange={() => handleReadyToShipChange(item.lineItemId, item.isReadyToShip)}
                                                                    disabled={updatingItemId === item.lineItemId}
                                                                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                                />
                                                                <span className="text-xs text-gray-700">
                                                                    准备发货 / Ready
                                                                </span>
                                                                {item.isReadyToShip && (
                                                                    <CheckCircleIcon className="h-4 w-4 text-green-600" />
                                                                )}
                                                        </label>
                                                    )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Dados do Cliente - Só na aba To be Shipped */}
                                        {currentStatus === 'ToBeShipped' && order.customer && (
                                            <div className="mb-6 bg-blue-50 p-4 rounded-lg">
                                                <h4 className="font-semibold text-blue-900 mb-3">
                                                    客户信息 / Customer Info
                                                </h4>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div>
                                                        <p className="text-sm text-gray-700 font-medium">
                                                            {order.customer.name}
                                                        </p>
                                                        <p className="text-sm text-gray-600">
                                                            CPF: {order.customer.cpf}
                                                        </p>
                                                        {order.customer.phone && (
                                                            <p className="text-sm text-gray-600">
                                                                Tel: {order.customer.phone}
                                                        </p>
                                                        )}
                                                    </div>
                                                    <div className="text-sm text-gray-700">
                                                        <p>{order.customer.address.line1}</p>
                                                        {order.customer.address.line2 && <p>{order.customer.address.line2}</p>}
                                                        <p>{order.customer.address.neighborhood}</p>
                                                        <p>{order.customer.address.city} - {order.customer.address.state}</p>
                                                        <p>CEP: {order.customer.address.zip}</p>
                                                        <p className="text-gray-600">{order.customer.address.country}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Input de Tracking - To be Shipped (UM SÓ PARA TODOS OS ITENS) */}
                                        {currentStatus === 'ToBeShipped' && (
                                            <div className="pt-4 border-t border-gray-200">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    追踪号码 / Tracking Number
                                                </label>
                                                <div className="flex space-x-2">
                                                    <input
                                                        type="text"
                                                        value={trackingInputs[order.orderNumber] || ''}
                                                        onChange={(e) =>
                                                            setTrackingInputs({
                                                                ...trackingInputs,
                                                                [order.orderNumber]: e.target.value
                                                            })
                                                        }
                                                        placeholder="输入追踪号 / Enter tracking"
                                                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                        disabled={updatingItemId === order.orderNumber}
                                                    />
                                                    <button
                                                        onClick={() => handleTrackingSubmit(order.orderNumber, order.items.map(i => i.lineItemId))}
                                                        disabled={updatingItemId === order.orderNumber}
                                                        className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                                                    >
                                                        {updatingItemId === order.orderNumber ? '...' : '发送 / Send'}
                                                    </button>
                                                </div>
                                                <p className="text-xs text-gray-500 mt-2">
                                                    Este código será aplicado a todos os {order.items.length} itens do pedido
                                                </p>
                                            </div>
                                        )}

                                        {/* Tracking - Shipped */}
                                        {currentStatus === 'Shipped' && order.items[0].trackingNumber && (
                                            <div className="pt-4 border-t border-gray-200">
                                                <div className="flex items-center space-x-2 text-green-700">
                                                    <CheckCircleIcon className="h-6 w-6" />
                                                    <div>
                                                        <p className="text-sm font-medium">已发货 / Shipped</p>
                                                        <p className="text-sm">
                                                            追踪号 / Tracking: <strong>{order.items[0].trackingNumber}</strong>
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        </div>
                                </div>
                        ))
                    )}
                    </div>
            )}
            </div>
        </div>
    );
};

export default SupplierPage;
